import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CaptionUploadModal from '../CaptionUploadModal';
import { useCaptionUpload } from '../../hooks/useCaptionUpload';
import UseToast from '../UseToast';

const LocationVideoProgress = ({ userId, locationId, currentLanguage, videoStatus,
  setVideoStatus }) => {

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [captionModalOpen, setCaptionModalOpen] = useState(false);
  const [location, setLocation] = useState(null);

  const { handleCaptionUpload } = useCaptionUpload();
  const { showMsgSuccessToast, showErrorToast } = UseToast();

  // SRT to VTT converter function
  const convertSrtToVtt = (srtContent) => {
    try {
      if (!srtContent || typeof srtContent !== 'string') {
        return '';
      }

      // Remove BOM if present
      srtContent = srtContent.replace(/^\uFEFF/, '');

      // Normalize line endings
      let vttContent = srtContent
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\n+/g, '\n')
        .trim();

      // Add VTT header
      vttContent = 'WEBVTT\n\n' + vttContent;

      // Convert timing format: comma to dot for milliseconds
      vttContent = vttContent.replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');

      // Remove SRT index numbers
      vttContent = vttContent.replace(/^\d+\s*$/gm, '');

      // Clean up empty lines
      vttContent = vttContent
        .split('\n')
        .filter(line => line.trim() !== '')
        .join('\n');

      return vttContent;
    } catch (error) {
      console.error('Error converting SRT to VTT:', error);
      return '';
    }
  };

  // Fetch video status for current location and language
  const fetchVideoStatus = async () => {
    if (!userId || !locationId || !currentLanguage) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `/api/wayfinder/locations/${locationId}/video-status?language=${currentLanguage}`
      );

      if (response.data.success) {
        // Find the video status for the current language
        const currentVideoStatus = response.data.data.find(
          status => status.language === currentLanguage
        );
        setVideoStatus(currentVideoStatus || null);
      } else {
        setError('Failed to fetch video status');
      }
    } catch (err) {
      console.error('Error fetching video status:', err);
      setError(err.response?.data?.message || 'Failed to fetch video status');
    } finally {
      setLoading(false);
    }
  };

  // Fetch location data to get caption info
  const fetchLocation = async () => {
    if (!locationId) return;

    try {
      const response = await axios.get(`/api/wayfinder/locations/${locationId}`);
      if (response.data.success) {
        setLocation(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching location:', err);
    }
  };

  const handleGeneratedVideoCaptionUpload = async (captionFile, convertSrtToVtt) => {
    if (!captionFile) {
      // Delete caption - use new auto-save endpoint
      try {
        const response = await axios.patch(
          `/api/wayfinder/locations/${locationId}/update-caption/${currentLanguage}`,
          { captionFile: '' }
        );

        if (response.data.success) {
          setLocation(response.data.data);
          showMsgSuccessToast('Caption file removed successfully!');
          // Refresh location data to update caption status
          await fetchLocation();
        }
      } catch (err) {
        console.error('Error removing caption:', err);
        showErrorToast('Failed to remove caption file!');
        throw err;
      }
      return;
    }

    // Upload caption file to S3 first
    try {
      let fileToUpload = captionFile;

      // Convert SRT to VTT if needed
      if (captionFile.name.endsWith('.srt')) {
        const srtText = await captionFile.text();
        const vttText = convertSrtToVtt(srtText);

        const vttFileName = captionFile.name.replace(/\.srt$/i, '.vtt');
        const vttBlob = new Blob([vttText], { type: 'text/vtt' });

        fileToUpload = new File([vttBlob], vttFileName, {
          type: 'text/vtt',
          lastModified: Date.now(),
        });
      }

      // Get pre-signed URL
      const urlResponse = await axios.get('/generate-signed-url', {
        params: {
          fileName: fileToUpload.name,
          fileType: fileToUpload.type || 'text/vtt',
        },
      });

      const { url } = urlResponse.data;

      // Upload to S3
      await axios.put(url, fileToUpload, {
        headers: {
          'Content-Type': fileToUpload.type || 'text/vtt',
        },
      });

      const fileUrl = url.split('?')[0];

      // Auto-save to database using new endpoint
      const response = await axios.patch(
        `/api/wayfinder/locations/${locationId}/update-caption/${currentLanguage}`,
        { captionFile: fileUrl }
      );

      if (response.data.success) {
        setLocation(response.data.data);
        showMsgSuccessToast('Caption file uploaded and saved successfully!');
        // Refresh location data to show caption status
        await fetchLocation();
      }
    } catch (error) {
      console.error('Caption upload failed:', error);
      showErrorToast('Failed to upload caption file!');
      throw error;
    }
  };

  // Generate video for current language
  const generateVideo = async () => {
    if (!userId || !locationId || !currentLanguage) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `/api/wayfinder/locations/${locationId}/generate-video`,
        {
          language: currentLanguage,
          resolution: '4k' // Default to 4K resolution
        }
      );

      if (response.data.success) {
        showMsgSuccessToast('Video generation started successfully!');
        // Refresh video status after generation starts
        setTimeout(() => {
          fetchVideoStatus();
        }, 1000);
      } else {
        const errorMsg = response.data.message || 'Failed to generate video';
        setError(errorMsg);
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error('Error generating video:', err);
      
      // Extract detailed error information
      let errorMessage = 'Failed to start video generation';
      let errorDetails = null;

      if (err.response?.data) {
        errorMessage = err.response.data.message || errorMessage;
        errorDetails = err.response.data.error || err.response.data.details;
        
        // Handle specific error types
        if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please check your HeyGen API key.';
        } else if (err.response.status === 402) {
          errorMessage = 'Insufficient credits. Please check your HeyGen account balance.';
        } else if (err.response.status === 429) {
          errorMessage = 'Rate limit exceeded. Please wait before trying again.';
        } else if (err.response.status === 409) {
          errorMessage = 'Video generation already in progress for this language.';
        } else if (err.response.status === 400) {
          errorMessage = err.response.data.message || 'Invalid request. Please check your settings.';
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = err.message || errorMessage;
      }

      // Combine error message with details if available
      const fullError = errorDetails ? `${errorMessage}\n\nDetails: ${errorDetails}` : errorMessage;
      
      setError(fullError);
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Retry failed video generation
  const retryVideoGeneration = async () => {
    if (!videoStatus?._id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `/api/language-videos/retry/${videoStatus._id}`
      );

      if (response.data.success) {
        showMsgSuccessToast('Video generation retry initiated!');
        // Refresh video status after retry
        setTimeout(() => {
          fetchVideoStatus();
        }, 1000);
      } else {
        const errorMsg = response.data.message || 'Failed to retry video generation';
        setError(errorMsg);
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error('Error retrying video generation:', err);
      
      // Extract detailed error information
      let errorMessage = 'Failed to retry video generation';
      let errorDetails = null;

      if (err.response?.data) {
        errorMessage = err.response.data.message || errorMessage;
        errorDetails = err.response.data.error || err.response.data.details;
        
        // Handle specific error types
        if (err.response.status === 404) {
          errorMessage = 'Video status not found. Please try generating a new video.';
        } else if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please check your HeyGen API key.';
        } else if (err.response.status === 402) {
          errorMessage = 'Insufficient credits. Please check your HeyGen account balance.';
        }
      }

      // Combine error message with details if available
      const fullError = errorDetails ? `${errorMessage}\n\nDetails: ${errorDetails}` : errorMessage;
      
      setError(fullError);
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get status display info
  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return {
          icon: '⏳',
          text: 'Pending',
          color: '#ffc107',
          bgColor: '#fff3cd',
          description: 'Video generation is queued'
        };
      case 'processing':
        return {
          icon: '🎬',
          text: 'Processing',
          color: '#17a2b8',
          bgColor: '#d1ecf1',
          description: 'Video is being generated'
        };
      case 'completed':
        return {
          icon: '✅',
          text: 'Completed',
          color: '#28a745',
          bgColor: '#d4edda',
          description: 'Video is ready'
        };
      case 'failed':
        return {
          icon: '❌',
          text: 'Failed',
          color: '#dc3545',
          bgColor: '#f8d7da',
          description: 'Video generation failed'
        };
      case 'obsolete':
        return {
          icon: '🔄',
          text: 'Obsolete',
          color: '#6c757d',
          bgColor: '#f8f9fa',
          description: 'Video was replaced by newer version'
        };
      default:
        return {
          icon: '❓',
          text: 'Unknown',
          color: '#6c757d',
          bgColor: '#f8f9fa',
          description: 'Unknown status'
        };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Download file helper function
  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      showErrorToast(`Failed to download ${filename}`);
    }
  };

  // Handle view/download video and caption
  const handleViewVideo = async () => {
    if (!videoStatus?.videoUrl) return;

    // Download video

    let videoUrl = null;

     if (location?.avatarVideosMultiLang) {
      if (location.avatarVideosMultiLang instanceof Map) {
        videoUrl = location.avatarVideosMultiLang.get(currentLanguage)?.fileUrl;
        
      } else if (typeof location.avatarVideosMultiLang === 'object') {
        
         videoUrl = location.avatarVideosMultiLang[currentLanguage]?.fileUrl;
      }
    }

    // const videoUrl = videoStatus.awsVideoUrl || videoStatus.videoUrl;
    const videoId = videoStatus.videoId;
    const videoFilename = `${videoId}_${currentLanguage}.mp4`;
    showMsgSuccessToast('Downloading video...');
    await downloadFile(videoUrl, videoFilename);

    // Check if caption file exists and download it
    let captionFile = null;
    if (location?.avatarVideosMultiLang) {
      if (location.avatarVideosMultiLang instanceof Map) {
        captionFile = location.avatarVideosMultiLang.get(currentLanguage)?.captionFile;
      } else if (typeof location.avatarVideosMultiLang === 'object') {
        captionFile = location.avatarVideosMultiLang[currentLanguage]?.captionFile;
      }
    }

    if (captionFile) {
      // Extract filename from URL or create a default one
      const captionFilename = captionFile.split('/').pop() || `${locationId}_${currentLanguage}_caption.vtt`;
      showMsgSuccessToast('Downloading caption file...');
      await downloadFile(captionFile, captionFilename);
    }
  };

  // Auto-refresh video status for processing videos
  useEffect(() => {
    fetchVideoStatus();
    fetchLocation();
  }, [userId, locationId, currentLanguage]);

  useEffect(() => {
    let interval;

    if (videoStatus?.status === 'processing' || videoStatus?.status === 'pending') {
      // Refresh every 10 seconds for processing/pending videos
      interval = setInterval(() => {
        fetchVideoStatus();
      }, 10000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [videoStatus?.status]);

  if (loading && !videoStatus) {
    return (
      <div className="video-progress-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Loading video status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    const errorParts = error.split('\n\nDetails:');
    const mainError = errorParts[0];
    const errorDetails = errorParts[1];

    return (
      <div className="video-progress-container">
        <div className="error-card-modern">
          <div className="error-icon-wrapper">
            <div className="error-icon-circle">
              <span className="error-icon-large">⚠️</span>
            </div>
          </div>
          
          <div className="error-content-modern">
            <h3 className="error-title-modern">Video Generation Failed</h3>
            <p className="error-message-modern">{mainError}</p>
            
            {errorDetails && (
              <div className="error-details-modern">
                <div className="error-details-label">
                  <span className="details-icon">ℹ️</span>
                  <span>Technical Details</span>
                </div>
                <div className="error-details-text">{errorDetails}</div>
              </div>
            )}
          </div>

          <div className="error-actions-modern">
            <button
              className="btn-retry-modern"
              onClick={generateVideo}
              disabled={loading}
            >
              <span className="btn-icon">{loading ? '⏳' : '🔄'}</span>
              <span>{loading ? 'Retrying...' : 'Try Again'}</span>
            </button>
            <button
              className="btn-dismiss-modern"
              onClick={() => setError(null)}
              disabled={loading}
            >
              <span className="btn-icon">✕</span>
              <span>Dismiss</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!videoStatus) {
    return (
      <div className="video-progress-container">
        {loading ? (
          <div className="generating-video-animation">
            <div className="video-generation-card">
              <div className="generation-icon-wrapper">
                <div className="generation-icon">🎬</div>
                <div className="pulse-ring"></div>
                <div className="pulse-ring-delayed"></div>
              </div>
              <div className="generation-text">
                <h3 className="generation-title">Generating Video</h3>
                <p className="generation-subtitle">Creating 4K video for {currentLanguage.toUpperCase()}</p>
              </div>
              <div className="progress-bar-container">
                <div className="progress-bar-animated"></div>
              </div>
              <div className="generation-steps">
                <div className="step step-active">
                  <span className="step-icon">✓</span>
                  <span className="step-text">Request sent</span>
                </div>
                <div className="step step-processing">
                  <span className="step-icon">⏳</span>
                  <span className="step-text">Processing...</span>
                </div>
                <div className="step">
                  <span className="step-icon">⏸</span>
                  <span className="step-text">Awaiting completion</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="no-video-status">
            <div className="no-video-info">
              <span className="no-video-icon">🎥</span>
              <div className="no-video-text">
                <div className="no-video-title">No video for {currentLanguage.toUpperCase()}</div>
                <div className="no-video-description">Generate a 4K video for this language</div>
              </div>
            </div>
            <button
              className="generate-btn"
              onClick={generateVideo}
              disabled={loading}
            >
              {loading ? '⏳ Generating...' : '🚀 Generate Video'}
            </button>
          </div>
        )}
      </div>
    );
  }

  const statusInfo = getStatusInfo(videoStatus.status);

  return (
    <div className="video-progress-container">
      <div className="video-status-card">
        <div className="status-header">
          <div className="status-info">
            <span className="status-icon">{statusInfo.icon}</span>
            <div className="status-text">
              <div className="status-title">
                Video Status: <span style={{ color: statusInfo.color }}>{statusInfo.text}</span>
              </div>
              <div className="status-description">{statusInfo.description}</div>
            </div>
          </div>

          {videoStatus.status === 'processing' && (
            <div className="processing-spinner">
              <div className="spinner"></div>
            </div>
          )}
        </div>

        <div className="status-details">
          <div className="detail-row">
            <span className="detail-label">Language:</span>
            <span className="detail-value">{currentLanguage.toUpperCase()}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Video ID:</span>
            <span className="detail-value">{videoStatus.videoId}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Created:</span>
            <span className="detail-value">{formatDate(videoStatus.createdAt)}</span>
          </div>

          {videoStatus.updatedAt !== videoStatus.createdAt && (
            <div className="detail-row">
              <span className="detail-label">Updated:</span>
              <span className="detail-value">{formatDate(videoStatus.updatedAt)}</span>
            </div>
          )}

          {videoStatus.duration > 0 && (
            <div className="detail-row">
              <span className="detail-label">Duration:</span>
              <span className="detail-value">{videoStatus.duration}s</span>
            </div>
          )}

          {videoStatus.error && (
            <div className="error-section-modern">
              <div className="error-badge">
                <span className="error-badge-icon">⚠️</span>
                <span className="error-badge-text">Error Occurred</span>
              </div>
              
              <div className="error-message-box">
                <div className="error-message-content">
                  <p className="error-main-text">{videoStatus.error}</p>
                  
                  {videoStatus.errorDetails && (
                    <details className="error-expandable">
                      <summary className="error-summary">
                        <span className="summary-icon">🔍</span>
                        <span>View Technical Details</span>
                      </summary>
                      <div className="error-technical-details">
                        <pre className="error-pre">{videoStatus.errorDetails}</pre>
                      </div>
                    </details>
                  )}
                </div>
              </div>

              {videoStatus.errorType && (
                <div className="error-type-badge">
                  <span className="type-label">Error Type:</span>
                  <span className="type-value">{videoStatus.errorType.replace(/_/g, ' ')}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="status-actions">
          {videoStatus.status === 'completed' && videoStatus.videoUrl && (
            <>
              <button
                onClick={handleViewVideo}
                className="action-btn view-btn"
                disabled={loading}
              >
                👁️ View & Download Video
              </button>
              <button
                className="action-btn caption-btn"
                onClick={() => setCaptionModalOpen(true)}
                disabled={loading}
              >
                📝 Upload Caption
              </button>
            </>
          )}

          {videoStatus.status === 'failed' && (
            <button
              className="action-btn retry-btn"
              onClick={retryVideoGeneration}
              disabled={loading}
            >
              {loading ? '⏳ Retrying...' : '🔄 Retry'}
            </button>
          )}

          <button
            className="action-btn refresh-btn"
            onClick={fetchVideoStatus}
            disabled={loading}
          >
            {loading ? '⏳' : '🔄'} Refresh
          </button>
        </div>

        {/* Caption status indicator */}
        {(() => {
          // Handle both Map and plain object structures
          let captionFile = null;

          if (location?.avatarVideosMultiLang) {
            // Check if it's a Map
            if (location.avatarVideosMultiLang instanceof Map) {
              captionFile = location.avatarVideosMultiLang.get(currentLanguage)?.captionFile;
            }
            // Check if it's a plain object
            else if (typeof location.avatarVideosMultiLang === 'object') {
              captionFile = location.avatarVideosMultiLang[currentLanguage]?.captionFile;
            }
          }

          console.log('Caption file check:', {
            currentLanguage,
            captionFile,
            avatarVideosMultiLang: location?.avatarVideosMultiLang,
            isMap: location?.avatarVideosMultiLang instanceof Map,
          });

          return captionFile ? (
            <div className="caption-status">
              <span className="caption-icon">✅</span>
              <span className="caption-text">Caption file uploaded</span>
            </div>
          ) : null;
        })()}
      </div>

      <style jsx>{`
        .video-progress-container {
          margin: 15px 0;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #dee2e6;
        }

        .generating-video-animation {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 300px;
        }

        .video-generation-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 16px;
          padding: 40px;
          text-align: center;
          box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
          max-width: 500px;
          width: 100%;
          animation: slideIn 0.5s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .generation-icon-wrapper {
          position: relative;
          display: inline-block;
          margin-bottom: 24px;
        }

        .generation-icon {
          font-size: 64px;
          animation: bounce 2s ease-in-out infinite;
          position: relative;
          z-index: 2;
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .pulse-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100px;
          height: 100px;
          border: 3px solid rgba(255, 255, 255, 0.6);
          border-radius: 50%;
          animation: pulse 2s ease-out infinite;
        }

        .pulse-ring-delayed {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100px;
          height: 100px;
          border: 3px solid rgba(255, 255, 255, 0.4);
          border-radius: 50%;
          animation: pulse 2s ease-out infinite 1s;
        }

        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(0.8);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.5);
            opacity: 0;
          }
        }

        .generation-text {
          margin-bottom: 24px;
        }

        .generation-title {
          color: white;
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 8px 0;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .generation-subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-size: 16px;
          margin: 0;
        }

        .progress-bar-container {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          height: 8px;
          overflow: hidden;
          margin-bottom: 24px;
        }

        .progress-bar-animated {
          height: 100%;
          background: linear-gradient(90deg, #fff, rgba(255, 255, 255, 0.7), #fff);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 10px;
        }

        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        .generation-steps {
          display: flex;
          justify-content: space-between;
          gap: 12px;
        }

        .step {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .step-active {
          background: rgba(255, 255, 255, 0.25);
          transform: scale(1.05);
        }

        .step-processing {
          background: rgba(255, 255, 255, 0.2);
        }

        .step-icon {
          font-size: 24px;
        }

        .step-processing .step-icon {
          animation: rotate 2s linear infinite;
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .step-text {
          color: white;
          font-size: 12px;
          font-weight: 500;
          text-align: center;
        }

        .loading-spinner, .processing-spinner {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #007bff;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #dc3545;
          background: #f8d7da;
          padding: 10px;
          border-radius: 6px;
          border: 1px solid #f5c6cb;
        }

        .error-icon {
          font-size: 18px;
        }

        /* Modern Error Card Styles */
        .error-card-modern {
          background: linear-gradient(135deg, #fff 0%, #fff5f5 100%);
          border-radius: 16px;
          padding: 0;
          border: 2px solid #ff6b6b;
          box-shadow: 0 8px 32px rgba(220, 53, 69, 0.2);
          overflow: hidden;
          animation: slideInError 0.4s ease-out;
        }

        @keyframes slideInError {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .error-icon-wrapper {
          background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
          padding: 32px;
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
          overflow: hidden;
        }

        .error-icon-wrapper::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          animation: pulse-error 3s ease-in-out infinite;
        }

        @keyframes pulse-error {
          0%, 100% {
            transform: scale(1);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.8;
          }
        }

        .error-icon-circle {
          width: 80px;
          height: 80px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          position: relative;
          z-index: 1;
        }

        .error-icon-large {
          font-size: 48px;
          animation: shake 0.5s ease-in-out;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px) rotate(-5deg); }
          75% { transform: translateX(5px) rotate(5deg); }
        }

        .error-content-modern {
          padding: 32px;
          text-align: center;
        }

        .error-title-modern {
          font-size: 24px;
          font-weight: 700;
          color: #dc3545;
          margin: 0 0 16px 0;
          letter-spacing: -0.5px;
        }

        .error-message-modern {
          font-size: 16px;
          color: #495057;
          line-height: 1.6;
          margin: 0 0 20px 0;
          font-weight: 500;
        }

        .error-details-modern {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          margin-top: 20px;
          border-left: 4px solid #ff6b6b;
          text-align: left;
        }

        .error-details-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #495057;
          margin-bottom: 12px;
          font-size: 14px;
        }

        .details-icon {
          font-size: 16px;
        }

        .error-details-text {
          font-size: 13px;
          color: #6c757d;
          font-family: 'Courier New', monospace;
          background: white;
          padding: 12px;
          border-radius: 8px;
          white-space: pre-wrap;
          word-break: break-word;
          border: 1px solid #e9ecef;
        }

        .error-actions-modern {
          display: flex;
          gap: 12px;
          padding: 24px 32px 32px;
          background: #f8f9fa;
        }

        .btn-retry-modern,
        .btn-dismiss-modern {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 24px;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .btn-retry-modern {
          background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
          color: white;
        }

        .btn-retry-modern:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
        }

        .btn-retry-modern:active:not(:disabled) {
          transform: translateY(0);
        }

        .btn-retry-modern:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-dismiss-modern {
          background: white;
          color: #6c757d;
          border: 2px solid #dee2e6;
        }

        .btn-dismiss-modern:hover:not(:disabled) {
          background: #f8f9fa;
          border-color: #adb5bd;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .btn-icon {
          font-size: 18px;
        }

        /* Error Section in Video Status Card */
        .error-section-modern {
          margin-top: 16px;
          padding: 16px;
          background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
          border-radius: 12px;
          border: 2px solid #ffcdd2;
        }

        .error-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #dc3545;
          color: white;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 12px;
          box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
        }

        .error-badge-icon {
          font-size: 14px;
        }

        .error-message-box {
          background: white;
          border-radius: 10px;
          padding: 16px;
          margin-bottom: 12px;
          border: 1px solid #ffcdd2;
        }

        .error-message-content {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .error-main-text {
          margin: 0;
          color: #721c24;
          font-size: 14px;
          line-height: 1.6;
          font-weight: 500;
        }

        .error-expandable {
          margin-top: 8px;
        }

        .error-summary {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #495057;
          transition: all 0.2s ease;
          user-select: none;
        }

        .error-summary:hover {
          background: #e9ecef;
        }

        .summary-icon {
          font-size: 14px;
        }

        .error-technical-details {
          margin-top: 8px;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 8px;
          border-left: 3px solid #dc3545;
        }

        .error-pre {
          margin: 0;
          font-family: 'Courier New', monospace;
          font-size: 12px;
          color: #495057;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .error-type-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: white;
          border-radius: 8px;
          font-size: 12px;
          border: 1px solid #ffcdd2;
        }

        .type-label {
          color: #6c757d;
          font-weight: 600;
        }

        .type-value {
          color: #dc3545;
          font-weight: 700;
          text-transform: capitalize;
        }

        /* Legacy error styles (kept for compatibility) */
        .error-card {
          background: white;
          border-radius: 8px;
          padding: 20px;
          border: 2px solid #dc3545;
          box-shadow: 0 4px 12px rgba(220, 53, 69, 0.15);
        }

        .error-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 2px solid #f8d7da;
        }

        .error-title {
          font-size: 20px;
          font-weight: 700;
          color: #dc3545;
        }

        .error-content {
          margin-bottom: 16px;
        }

        .error-message-text {
          color: #721c24;
          font-size: 15px;
          line-height: 1.5;
          margin-bottom: 8px;
          white-space: pre-wrap;
        }

        .error-details {
          background: #fff5f5;
          padding: 12px;
          border-radius: 6px;
          border-left: 4px solid #dc3545;
          margin-top: 12px;
          font-size: 13px;
          color: #666;
          font-family: monospace;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .error-actions {
          display: flex;
          gap: 10px;
        }

        .retry-btn-primary {
          flex: 1;
          padding: 10px 20px;
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .retry-btn-primary:hover:not(:disabled) {
          background: #c82333;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
        }

        .retry-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .dismiss-btn {
          padding: 10px 20px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .dismiss-btn:hover:not(:disabled) {
          background: #545b62;
        }

        .error-details-container {
          display: flex;
          flex-direction: column;
          gap: 8px;
          width: 100%;
        }

        .error-label {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .error-icon-small {
          font-size: 16px;
        }

        .error-content-box {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .error-additional-details {
          background: #fff5f5;
          padding: 10px;
          border-radius: 4px;
          border-left: 3px solid #dc3545;
          font-size: 12px;
        }

        .error-additional-details strong {
          display: block;
          margin-bottom: 6px;
          color: #721c24;
        }

        .error-details-pre {
          margin: 0;
          font-family: monospace;
          font-size: 11px;
          color: #666;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .no-video-status {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
        }

        .no-video-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .no-video-icon {
          font-size: 24px;
        }

        .no-video-text {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .no-video-title {
          font-weight: 600;
          color: #333;
        }

        .no-video-description {
          font-size: 14px;
          color: #666;
        }

        .video-status-card {
          background: white;
          border-radius: 8px;
          padding: 15px;
          border: 1px solid #dee2e6;
        }

        .status-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 15px;
        }

        .status-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .status-icon {
          font-size: 24px;
        }

        .status-text {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .status-title {
          font-weight: 600;
          color: #333;
        }

        .status-description {
          font-size: 14px;
          color: #666;
        }

        .status-details {
          margin-bottom: 15px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 0;
          border-bottom: 1px solid #f1f3f4;
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-label {
          font-weight: 500;
          color: #666;
          font-size: 14px;
        }

        .detail-value {
          color: #333;
          font-size: 14px;
          font-family: monospace;
        }

        .error-row {
          background: #fff5f5;
          padding: 8px;
          border-radius: 4px;
          border: 1px solid #fed7d7;
        }

        .error-text {
          color: #dc3545;
          font-weight: 500;
        }

        .status-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s ease;
        }

        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .view-btn {
          background: #28a745;
          color: white;
          border: none;
        }

        .view-btn:hover:not(:disabled) {
          background: #218838;
          color: white;
          text-decoration: none;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .retry-btn {
          background: #dc3545;
          color: white;
        }

        .retry-btn:hover:not(:disabled) {
          background: #c82333;
        }

        .refresh-btn {
          background: #6c757d;
          color: white;
        }

        .refresh-btn:hover:not(:disabled) {
          background: #545b62;
        }

        .generate-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }

        .generate-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .generate-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .caption-btn {
          background: #17a2b8;
          color: white;
        }

        .caption-btn:hover:not(:disabled) {
          background: #138496;
        }

        .caption-status {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
          padding: 8px 12px;
          background: #d4edda;
          border: 1px solid #c3e6cb;
          border-radius: 6px;
        }

        .caption-icon {
          font-size: 16px;
        }

        .caption-text {
          font-size: 14px;
          color: #155724;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .video-generation-card {
            padding: 30px 20px;
          }

          .generation-title {
            font-size: 24px;
          }

          .generation-subtitle {
            font-size: 14px;
          }

          .generation-steps {
            flex-direction: column;
          }

          .step {
            flex-direction: row;
            justify-content: flex-start;
          }

          .no-video-status {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .status-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .status-actions {
            flex-direction: column;
          }

          .action-btn {
            width: 100%;
            justify-content: center;
          }

          /* Modern Error Card Mobile Styles */
          .error-card-modern {
            border-radius: 12px;
          }

          .error-icon-wrapper {
            padding: 24px;
          }

          .error-icon-circle {
            width: 64px;
            height: 64px;
          }

          .error-icon-large {
            font-size: 36px;
          }

          .error-content-modern {
            padding: 24px 20px;
          }

          .error-title-modern {
            font-size: 20px;
          }

          .error-message-modern {
            font-size: 15px;
          }

          .error-actions-modern {
            flex-direction: column;
            padding: 20px;
          }

          .btn-retry-modern,
          .btn-dismiss-modern {
            width: 100%;
          }

          .error-section-modern {
            padding: 12px;
          }

          .error-message-box {
            padding: 12px;
          }

          .error-details-text {
            font-size: 12px;
            padding: 10px;
          }
        }
      `}</style>

      {/* Caption Upload Modal for Generated Video */}
      <CaptionUploadModal
        isOpen={captionModalOpen}
        onClose={() => setCaptionModalOpen(false)}
        onUpload={handleGeneratedVideoCaptionUpload}
        existingCaptionUrl={location?.avatarVideosMultiLang[currentLanguage]?.captionFile}
        buttonColor="#007bff"
        showMsgSuccessToast={showMsgSuccessToast}
        showErrorToast={showErrorToast}
      />
    </div>
  );
};

export default LocationVideoProgress;